import { useState, useEffect } from "react";
import Axios from "axios";
import MultiStepLayout from "./components/MultiStepLayout";
import Result from "./components/Result";
import jstz from 'jstz';
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import 'react-phone-input-2/lib/style.css';
import './globals.css';
import { useTranslation } from "react-i18next";
import { isPossiblePhoneNumber } from 'react-phone-number-input'

function App({ leadFormId, language }) {
  const id = leadFormId;
  const timezone = jstz.determine();

  // Regex pattern for first and last name validation
  const nameRegex = /^(?!.*[()])[A-Za-z\s'\-\.^]+$/;

  const { i18n, t } = useTranslation();

  const [step, setStep] = useState(1);
  const [leadForm, setLeadForm] = useState();
  const [loaded, setLoaded] = useState(false);
  const [lead, setLead] = useState({ errors: {} });
  const [submitting, setSubmitting] = useState(false);
  const [geoLocation, setGeoLocation] = useState({ city: '', state: '', countryName: '', countryCode: '', phoneCode: 'US' });
  const [step1Valid, setStep1Valid] = useState(false);
  const [step2Valid, setStep2Valid] = useState(false);
  const [useMetricSystem, setUseMetricSystem] = useState(true);
  const [isMobile, setIsMobile] = useState(true);
  const [activeElement, setActiveElement] = useState(null);

  const fetchGeoLocationData = async () => {
    try {
      const res = await Axios.get('https://geolocation-db.com/json/')
      setGeoLocation({ city: res.data.city, state: res.data.state, countryName: res.data.country_name, phoneCode: res.data.country_code, countryCode: res.data.country_code.toLowerCase() });

      if (['us'].includes(res.data.country_code.toLowerCase())) {
        setUseMetricSystem(false);
      }

      if (['us', 'ca'].includes(res.data.country_code.toLowerCase())) {
        setLead({ ...lead, language: "English" });
      }
    } catch (e) {
      // console.log(e);
    }
  }

  useEffect(() => {
    i18n.changeLanguage(language || 'en');

    setFormWidth();
    fetchGeoLocationData();
  }, []);

  useEffect(() => {
    window.addEventListener("resize", () => {
      setFormWidth();
    });
    return () => {
      window.removeEventListener("resize", () => {
        setFormWidth();
      })
    }
  }, []);

  const setFormWidth = () => {
    const containerWidth = document.querySelector(`[data-pulse-form-id="${leadFormId}"]`)?.offsetWidth;

    if (containerWidth) {
      containerWidth < 750 ? setIsMobile(true) : setIsMobile(false);
    }
  }

  const onChange = (name, value) => {
    setLead({ ...lead, [name]: value });
    if (lead.errors[name]) {
      delete lead.errors[name];
    }
    if (step === 1) validateStep1({ ...lead, [name]: value });
    if (step === 2) validateStep2({ ...lead, [name]: value });
  }

  const onBlur = (name, value) => {
    if (step === 1) {
      validateStep1Fields(name, value);
      validateStep1({ ...lead, [name]: value });
    }
    if (step === 2) {
      validateStep2Fields(name, value);
      validateStep2({ ...lead, [name]: value });
    }
  }

  const validateStep1Fields = (name, value) => {
    if (name === 'fullName') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, fullName: t('lead.errors.required')}});
      } else if (value.split(' ').length < 2) {
        setLead({...lead, errors: {...lead.errors, fullName: t('lead.errors.lastName')}});
      } else if (!nameRegex.test(value)) {
      setLead({
        ...lead,
        errors: {
          ...lead.errors,
          fullName:
            t('lead.errors.nameValidation'),
        },
      });
    }
  }

    if (name === 'email') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, email: t('lead.errors.required')}});
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
        setLead({...lead, errors: {...lead.errors, email: t('lead.errors.email')}});
      }
    }

    if (name === 'phoneNumber') {
      if (!value) {
        setLead({ ...lead, errors: { ...lead.errors, phoneNumber: t('lead.errors.required') } });
      } else if (!isPossiblePhoneNumber(value)) {
        setLead({ ...lead, errors: { ...lead.errors, phoneNumber: t('lead.errors.phoneNumber') } });
      }
    }

    if (name === 'procedure') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, procedure: t('lead.errors.required')}});
      }
    }

    if (name === 'language') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, language: t('lead.errors.required')}});
      }
    }
  };

  const validateStep2Fields = (name, value) => {
    if (leadForm.questions === 'Optional') return;

    if (name === 'height') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, height: t('lead.errors.required')}});
      }
    }

    if (name === 'weight') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, weight: t('lead.errors.required')}});
      }
    }
  };

  const validateStep1 = (lead) => {
    if (!lead.fullName) {
      setStep1Valid(false);
      return (false);
    } else if (lead.fullName?.split(' ').length < 2) {
      setStep1Valid(false);
      return (false);
    } else if (!nameRegex.test(lead.fullName)) {
      setStep1Valid(false);
      return (false);
    }

    if (!lead.email) {
      setStep1Valid(false);
      return (false);
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(lead.email)) {
      setStep1Valid(false);
      return (false);
    }

    if (!lead.phoneNumber) {
      setStep1Valid(false);
      return (false);
    }

    if (!lead.procedure) {
      setStep1Valid(false);
      return (false);
    }

    if (!lead.language) {
      setStep1Valid(false);
      return (false);
    }

    setStep1Valid(true);
    return (true);
  };

  const validateStep2 = (lead) => {
    if (leadForm.questions === 'Optional') {
      setStep2Valid(true);
      return (true);
    }

    if (!lead.passport) {
      setStep2Valid(false);
      return (false);
    }

    if (!lead.requireFinancing) {
      setStep2Valid(false);
      return (false);
    }

    if (leadForm.showCustomQuestion) {
      if (!lead.customQuestionAnswer) {
        setStep2Valid(false);
        return (false);
      }
    }

    if (leadForm.showWeightAndHeight) {
      if (useMetricSystem) {
        if (!lead.height) {
          setStep2Valid(false);
          return (false);
        }
      } else {
        if (!lead.height.includes(' ft') || !lead.height.includes(' in')) {
          setStep2Valid(false);
          return (false);
        }
      }

      if (!lead.weight) {
        setStep2Valid(false);
        return (false);
      }
    }

    if (!lead.howSoon) {
      setStep2Valid(false);
      return (false);
    }

    setStep2Valid(true);
    return (true);
  };

  useEffect(() => {
    if (id) {
      Axios.get(`${process.env.REACT_APP_API_URL}/api/v1/lead_forms/${id}?locale=${language}`).then(
        (response) => {
          setLeadForm(response.data.lead_form);
          setLoaded(true);

          if (response.data.lead_form.questions === 'Optional') setStep2Valid(true);
        }
      );
    }
  }, [id]);

  const submitStep1 = () => {
    if (!validateStep1(lead)) return;
    setSubmitting(true);

    lead.timeZone = timezone.name();
    lead.city = geoLocation.city;
    lead.state = geoLocation.state;
    lead.country = geoLocation.countryName;
    lead.url = window.location.href;
    lead.leadFormId = leadFormId;
    lead.channelId = leadForm.channelId;
    lead.referringUrl = document.referrer;

    Axios.post(`${process.env.REACT_APP_API_URL}/api/v1/leads`, { lead: lead }).then(
      (response) => {
        setSubmitting(false);
        setLead({ ...lead, uuid: response.data.lead.uuid });

        if (leadForm.questions === 'Hidden' && leadForm.redirectUrl) {
          window.location = leadForm.redirectUrl;
        } else {
          leadForm.questions === 'Hidden' ? setStep(3) : setStep(2);
        }
      }
    ).catch((err) => {
      setSubmitting(false);
      console.log('error', err.response.data)
      if (err.response) {
        if (err.response.data.error === 'Phone number is invalid') {
        alert(t('invalidPhoneNumber'));
        } else {
        alert(err.response.data.error);}}
    });
  }

  const submitStep2 = () => {
    setSubmitting(true);
    if (leadForm.showCustomQuestion) lead.customQuestion = leadForm.customQuestion;
    if (useMetricSystem && lead.height) {
      lead.height = `${lead.height} cm`;
    }
    if (lead.weight) lead.weight = `${lead.weight} ${useMetricSystem ? 'kg' : 'lbs'}`;

    Axios.patch(`${process.env.REACT_APP_API_URL}/api/v1/leads/${lead.uuid}`, { lead: lead }).then(
      (response) => {
        if (leadForm.redirectUrl) {
          setSubmitting(false);
          window.location = leadForm.redirectUrl;
        } else {
          setSubmitting(false);
          setStep(3);
        }
      }
    ).catch((err) => {
      setSubmitting(false);
      if (err.response) alert(err.response.data.error);
    });
  }

  if (loaded) {
    if (step === 1)
      return (
        <Step1
          leadForm={leadForm}
          lead={lead}
          onChange={onChange}
          onBlur={onBlur}
          step1Valid={step1Valid}
          submitStep1={submitStep1}
          submitting={submitting}
          countryCode={geoLocation.countryCode}
          isMobile={isMobile}
          geoLocation={geoLocation}
          activeElement={activeElement}
          setActiveElement={setActiveElement}
        />
      );
    else if (step === 2) {
      return (
        <Step2
          leadForm={leadForm}
          lead={lead}
          onChange={onChange}
          onBlur={onBlur}
          step2Valid={step2Valid}
          submitStep2={submitStep2}
          submitting={submitting}
          useMetricSystem={useMetricSystem}
          isMobile={isMobile}
          activeElement={activeElement}
          setActiveElement={setActiveElement}
        />
      );
    } else if (step === 3) {
      return (
        <MultiStepLayout backgroundColor={leadForm.backgroundColor} foregroundColor={leadForm.titleColor} formBackgroundColor={leadForm.formBackgroundColor} isMobile={isMobile} showPoweredBy={true}>
          <Result successMessage={leadForm.successMessage} backgroundColor={leadForm.backgroundColor} isMobile={isMobile} />
        </MultiStepLayout>
      )
    }
  }
}

export default App;
